/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/useAssignment.ts
 * FILE_ID: EMAILHUB-ALLOCATION-ASSIGNMENT-001-HOOK
 * PURPOSE: Owns assignee derivation and assignment mutations for the rail.
 */
import { useCallback, useMemo } from 'react'

import type {
  AllocationMember,
  AssignmentHookInput,
  AssignmentViewModel,
} from './types'

export function useAssignment({
  emailId,
  assignment,
  members,
  busyKey,
  setBusyKey,
  showPicker,
  setShowPicker,
  setEmailAssignment,
  clearEmailAssignment,
  onAssignmentChanged,
  toast,
}: AssignmentHookInput): AssignmentViewModel {
  const assigneeUserId = assignment?.assignee_user_id ?? null

  const pickAssignee = useCallback(
    async (member: AllocationMember | null) => {
      if (busyKey) return
      setBusyKey(`assignee:${member?.id ?? 'none'}`)
      setShowPicker(false)
      try {
        if (member === null && assignment) {
          // Operator picked "Clear" — DELETE the row entirely.
          await clearEmailAssignment(emailId)
          onAssignmentChanged?.(null)
          toast('Assignment cleared', 'success')
          return
        }
        const next = await setEmailAssignment(emailId, {
          assignee_user_id: member ? member.id : null,
        })
        onAssignmentChanged?.(next)
        toast(
          member ? `Assigned to ${member.name}` : 'Assignee cleared',
          'success',
        )
      } catch (err) {
        toast(
          `Could not assign: ${(err as Error).message || 'unknown error'}`,
          'error',
        )
      } finally {
        setBusyKey(null)
      }
    },
    [
      busyKey,
      assignment,
      emailId,
      setEmailAssignment,
      clearEmailAssignment,
      onAssignmentChanged,
      toast,
      setBusyKey,
      setShowPicker,
    ],
  )

  const assigneeMember = useMemo(() => {
    if (!assigneeUserId) return null
    return members.find((member) => member.id === assigneeUserId) ?? null
  }, [members, assigneeUserId])

  return {
    assignment,
    members,
    busyKey,
    showPicker,
    setShowPicker,
    assigneeUserId,
    assigneeMember,
    pickAssignee,
  }
}
